import React, { useState } from "react";
import {
  View,
  TouchableOpacity,
  Text,
  StyleSheet,
  Platform,
  ActivityIndicator,
} from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";

// Define __DEV__ if it doesn't exist (for web environments)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const __DEV__: boolean =
  typeof (globalThis as any).__DEV__ !== "undefined"
    ? (globalThis as any).__DEV__
    : process.env.NODE_ENV !== "production";

interface TouchPointActionProps {
  onComplete: () => boolean; // Return boolean for validation result
  onSaveForLater?: () => void;
  onBackToDashboard?: () => void;
  selectedTouchPoint?: any;
}

const TouchPointAction: React.FC<TouchPointActionProps> = ({
  onComplete,
  onSaveForLater,
  onBackToDashboard,
  selectedTouchPoint,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPdfExporting, setIsPdfExporting] = useState(false);

  // Mock state for touchpoint status - in a real app, this would come from Redux
  const [touchPointStatus, setTouchPointStatus] = useState<
    "new" | "in-progress" | "completed"
  >("new");

  // Function to handle PDF export via email
  const handlePdfExport = async () => {
    if (isPdfExportDisabled) {
      return;
    }

    try {
      setIsPdfExporting(true);

      // Simulate PDF export
      await new Promise((resolve) => setTimeout(resolve, 1500));

      console.log("Exporting PDF for touchpoint:", selectedTouchPoint);

      alert("PDF export email sent successfully!");
    } catch (error) {
      console.error("PDF Export Error:", error);
      alert("Failed to send PDF export email");
    } finally {
      setIsPdfExporting(false);
    }
  };

  const handleSubmit = async (action: "save" | "complete") => {
    try {
      // First call the validation function provided by the parent component
      const isValid = onComplete();

      // If validation failed, exit early
      if (!isValid) {
        return;
      }

      // Set submitting state to true to disable buttons
      setIsSubmitting(true);

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1500));

      if (action === "complete") {
        setTouchPointStatus("completed");
        alert("Touchpoint completed successfully!");
      } else {
        setTouchPointStatus("in-progress");
        if (onSaveForLater) {
          onSaveForLater();
        }
        alert("Touchpoint saved for later!");
      }

      // For demo purposes, navigate back to dashboard after a short delay
      if (action === "complete" && onBackToDashboard) {
        setTimeout(() => {
          onBackToDashboard();
        }, 1000);
      }
    } catch (error) {
      console.error("Submit error:", error);
      alert("An error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Determine if the PDF export box should be disabled (when not completed yet)
  const isPdfExportDisabled = touchPointStatus !== "completed";

  return (
    <View style={styles.actionRow}>
      <View style={styles.buttonColumn}>
        {/* PDF Export Button */}
        <TouchableOpacity
          style={[
            styles.customButton,
            styles.buttonFlex,
            styles.pdfExportButton,
            isPdfExportDisabled ? styles.disabledButton : {},
            Platform.OS === "web" && !isPdfExportDisabled
              ? { cursor: "pointer" as any }
              : {},
          ]}
          onPress={isPdfExportDisabled ? () => {} : handlePdfExport}
          disabled={isPdfExportDisabled}
          accessibilityState={{ disabled: isPdfExportDisabled }}
          accessibilityLabel={
            isPdfExportDisabled
              ? "Export to PDF (disabled until completed)"
              : isPdfExporting
              ? "Sending PDF export email"
              : "Export to PDF"
          }
        >
          <View style={styles.buttonContent}>
            {isPdfExporting ? (
              <ActivityIndicator
                size="small"
                color="#FFF"
                style={styles.icon}
              />
            ) : (
              <MaterialIcons
                name="picture-as-pdf"
                size={20}
                color="white"
                style={styles.icon}
              />
            )}
            <Text
              style={[
                styles.buttonText,
                isPdfExportDisabled && styles.buttonTextDisabled,
              ]}
            >
              {isPdfExportDisabled
                ? "PDF Export Not Available"
                : isPdfExporting
                ? "Sending Email..."
                : "Export to PDF"}
            </Text>
          </View>
        </TouchableOpacity>

        {/* Save for Later Button */}
        <TouchableOpacity
          style={[
            styles.customButton,
            styles.buttonFlex,
            isSubmitting || touchPointStatus === "completed"
              ? styles.disabledButton
              : styles.saveButton,
            Platform.OS === "web" &&
            !(isSubmitting || touchPointStatus === "completed")
              ? { cursor: "pointer" as any }
              : {},
          ]}
          onPress={() => handleSubmit("save")}
          disabled={isSubmitting || touchPointStatus === "completed"}
          accessibilityState={{
            disabled: isSubmitting || touchPointStatus === "completed",
          }}
          accessibilityLabel={
            isSubmitting
              ? "Save for later (disabled during submission)"
              : touchPointStatus === "completed"
              ? "Save for later (disabled for completed touchpoint)"
              : "Save for later"
          }
        >
          <View style={styles.buttonContent}>
            {isSubmitting ? (
              <ActivityIndicator
                size="small"
                color="#DDD"
                style={styles.icon}
              />
            ) : (
              <MaterialIcons
                name="save"
                size={20}
                color="white"
                style={styles.icon}
              />
            )}
            <Text
              style={[
                styles.buttonText,
                isSubmitting && styles.buttonTextDisabled,
              ]}
            >
              {isSubmitting ? "Please wait..." : "Save for later"}
            </Text>
          </View>
        </TouchableOpacity>

        {/* Complete Button */}
        <TouchableOpacity
          style={[
            styles.customButton,
            styles.buttonFlex,
            isSubmitting || touchPointStatus === "completed"
              ? styles.disabledButton
              : styles.completeButton,
            Platform.OS === "web" &&
            !(isSubmitting || touchPointStatus === "completed")
              ? { cursor: "pointer" as any }
              : {},
          ]}
          onPress={() => handleSubmit("complete")}
          disabled={isSubmitting || touchPointStatus === "completed"}
          accessibilityState={{
            disabled: isSubmitting || touchPointStatus === "completed",
          }}
          accessibilityLabel={
            isSubmitting
              ? "Complete form (disabled during submission)"
              : touchPointStatus === "completed"
              ? "Complete form (disabled for completed touchpoint)"
              : "Complete form"
          }
        >
          <View style={styles.buttonContent}>
            {isSubmitting ? (
              <ActivityIndicator
                size="small"
                color="white"
                style={styles.icon}
              />
            ) : (
              <MaterialIcons
                name="check-circle"
                size={20}
                color="white"
                style={styles.icon}
              />
            )}
            <Text style={styles.buttonText}>
              {isSubmitting ? "Submitting..." : "Complete"}
            </Text>
          </View>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  actionRow: {
    flexDirection: Platform.OS === "web" ? "row" : "column",
    flexWrap: "wrap",
    marginTop: 30,
    borderTopWidth: 1,
    borderTopColor: "#dee2e6",
    paddingTop: 24,
    gap: 20,
    alignItems: "center",
    width: "100%",
  },
  buttonColumn: {
    flex: 2,
    flexDirection: "row",
    gap: 10,
    minWidth: 0,
    width: Platform.OS === "web" ? "auto" : "100%",
  },
  buttonFlex: {
    flex: 1,
    minWidth: 0, // Allow buttons to shrink if needed
  },
  customButton: {
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 6,
    backgroundColor: "#ccc",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  saveButton: {
    backgroundColor: "#FF9D2F", // Orange for save
  },
  completeButton: {
    backgroundColor: "#008242", // Green for complete
  },
  disabledButton: {
    opacity: 1,
    backgroundColor: "#999",
    borderWidth: 1,
    borderColor: "#ccc",
    shadowOpacity: 0.1,
    ...(Platform.OS === "web" ? { cursor: "not-allowed" as any } : {}),
  },
  pdfExportButton: {
    backgroundColor: "#D32F2F", // Red for PDF export
  },
  buttonContent: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  icon: {
    marginRight: 8,
  },
  buttonText: {
    color: "white",
    fontSize: 16,
    fontWeight: "600",
  },
  buttonTextDisabled: {
    color: "#FFFFFF",
  },
});

export default TouchPointAction;
